import { Layout } from '../components/Layout';
import { useState, useEffect } from 'react';
import axios from 'axios';
import { BarChart3, TrendingUp } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeader = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
});

export default function Reports({ user, onLogout }) {
  const [dailyData, setDailyData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchReportData();
  }, []);

  const fetchReportData = async () => {
    try {
      const res = await axios.get(`${API}/dashboard/daily`, getAuthHeader());
      setDailyData(res.data);
    } catch (error) {
      console.error('Failed to fetch report data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Layout user={user} onLogout={onLogout}>
        <div className="flex items-center justify-center h-full">
          <div className="text-stone-400">Loading...</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="p-8">
        <div className="mb-8">
          <h1 className="font-heading text-4xl font-bold text-stone-900">Reports & Analytics</h1>
          <p className="text-stone-600 mt-1">Production and operational insights</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          <div className="bg-white border border-stone-200 rounded-sm shadow-sm p-6">
            <div className="flex items-center gap-3 mb-4">
              <BarChart3 className="text-primary" size={24} />
              <h3 className="font-heading text-lg font-semibold text-stone-800">Production Summary</h3>
            </div>
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-stone-600">Total Batches</span>
                <span className="font-mono font-bold text-2xl text-primary">{dailyData?.production?.total_batches || 0}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-stone-600">Total Quantity</span>
                <span className="font-mono font-bold text-2xl text-primary">{dailyData?.production?.total_quantity?.toFixed(0) || 0}</span>
              </div>
            </div>
          </div>

          <div className="bg-white border border-stone-200 rounded-sm shadow-sm p-6">
            <div className="flex items-center gap-3 mb-4">
              <TrendingUp className="text-functional-success" size={24} />
              <h3 className="font-heading text-lg font-semibold text-stone-800">Transfer Activity</h3>
            </div>
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-stone-600">Total Transfers</span>
                <span className="font-mono font-bold text-2xl text-functional-success">{dailyData?.transfers?.total || 0}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-stone-600">Pending</span>
                <span className="font-mono font-bold text-functional-warning">{dailyData?.transfers?.pending || 0}</span>
              </div>
            </div>
          </div>

          <div className="bg-white border border-stone-200 rounded-sm shadow-sm p-6">
            <div className="flex items-center gap-3 mb-4">
              <BarChart3 className="text-functional-error" size={24} />
              <h3 className="font-heading text-lg font-semibold text-stone-800">Wastage Impact</h3>
            </div>
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-stone-600">Total Cost</span>
                <span className="font-mono font-bold text-2xl text-functional-error">₹{dailyData?.wastage?.total_cost?.toFixed(0) || 0}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-stone-600">Quantity</span>
                <span className="font-mono font-bold text-functional-error">{dailyData?.wastage?.total_quantity?.toFixed(1) || 0}</span>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white border border-stone-200 rounded-sm shadow-sm p-6">
          <h2 className="font-heading text-2xl font-semibold text-stone-800 mb-6">Production Batches Detail</h2>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-stone-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-stone-500 uppercase">Batch ID</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-stone-500 uppercase">Type</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-stone-500 uppercase">Planned</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-stone-500 uppercase">Actual</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-stone-500 uppercase">Wastage</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-stone-500 uppercase">Net Usable</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-stone-500 uppercase">Efficiency</th>
                </tr>
              </thead>
              <tbody>
                {dailyData?.production?.batches?.map((batch) => {
                  const efficiency = ((batch.net_usable / batch.planned_yield) * 100).toFixed(1);
                  return (
                    <tr key={batch.batch_id} className="border-b border-stone-100 hover:bg-stone-50">
                      <td className="px-4 py-3 font-mono text-sm">{batch.batch_id}</td>
                      <td className="px-4 py-3 capitalize">{batch.type}</td>
                      <td className="px-4 py-3 font-mono">{batch.planned_yield}</td>
                      <td className="px-4 py-3 font-mono">{batch.actual_yield}</td>
                      <td className="px-4 py-3 font-mono text-functional-error">{batch.damaged + batch.rejected}</td>
                      <td className="px-4 py-3 font-mono font-semibold">{batch.net_usable}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 rounded-sm text-xs font-mono font-medium ${
                          parseFloat(efficiency) >= 90
                            ? 'bg-functional-success/10 text-functional-success'
                            : parseFloat(efficiency) >= 75
                            ? 'bg-functional-warning/10 text-functional-warning'
                            : 'bg-functional-error/10 text-functional-error'
                        }`}>
                          {efficiency}%
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {!dailyData?.production?.batches?.length && (
              <div className="text-center py-12 text-stone-400">No production data available</div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}
